import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import MetaTags from 'react-meta-tags';
import {
  Container,
  Button,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  Alert,
  Nav,
  NavItem,
  TabContent,
  TabPane,
} from 'reactstrap';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import paginationFactory, {
  PaginationProvider,
  PaginationListStandalone,
  SizePerPageDropdownStandalone,
} from 'react-bootstrap-table2-paginator';
import ToolkitProvider from 'react-bootstrap-table2-toolkit';
import BootstrapTable from 'react-bootstrap-table-next';
import 'react-datepicker/dist/react-datepicker.css';
import axios from 'axios';
import styled from 'styled-components';

import { useAuth } from 'hooks/useAuth';
import { getFirebaseBackend } from '../../helpers/firebaseHelper';
import LabelGroup from '../../components/Label-Group';
import TextInput from '../../components/Text-Input';
import SelectInput from '../../components/Select-Input';
import DateInput from '../../components/Date-Input';
import SpinnerInput from '../../components/Spinner-Input';
import Breadcrumbs from '../../components/Breadcrumb';
import { getFormValue, normalizeCode, copyToClipBoard } from '../../utils/';
import {
  accountServices,
  accountModules,
  countrySelectOptions,
  languageSelectOptions,
} from '../../constants';
import CardInputGoogleDrive from './components/Card-Input-Google-Drive';
import CardInputCMS from './components/Card-Input-CMS';
import Shimmer from '../../components/Shimmer';
import { FILE_SIZE_5MB } from 'constants/fileSize';

const EditAccount = () => {
  const formConfig = {
    defaultValues: {
      id: '',
      createdAt: '',
      name: '',
      status: [],
      beginDate: '',
      contractTime: '',
      oneTime: false,
      services: [],
      modules: [],
      gscLink: '',
      gaLink: '',
      internalTeam: [],
      approvalTeam: [],
      reviewer: '',
      contentManager: '',
      contactPoint: [],
      country: '',
      language: '',
      gDriveFolders: '',
      cmsAuth: { link: '', login: '', base64: '', type: '', key: '' },
    },
    shouldFocusError: true,
  };
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const accountParam = searchParams.get('id');
  const { user } = useAuth();
  const form = useForm(formConfig);
  const {
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors },
    clearErrors,
    setValue,
    getValues,
  } = form;
  const { t: translate } = useTranslation();
  const firebaseHelper = getFirebaseBackend();
  const [file, setFile] = useState(null);
  const [updateSuccessfull, setUpdateSuccessfull] = useState(true);
  const [modal, setModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorModal, setErrorModal] = useState(false);
  const [teamData, setTeamData] = useState([]);
  const [selectKey, setSelectKey] = useState(0);
  const [cmsType, setCmsType] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [oldModules, setOldModules] = useState([]);
  const [domain, setDomain] = useState('');
  const [changedFile, setChangedFile] = useState(false);
  const [gDriveFolders, setGDriveFolders] = useState({});
  const [activeNav, setActiveNav] = useState('1');
  const [isAccountDeactivated, setIsAccountDeactivated] = useState(true);

  const min = Math.ceil(10000);
  const max = Math.floor(99999);
  const [num1, setNum1] = useState(Math.floor(Math.random() * (max - min + 1)) + min);
  const [num2, setNum2] = useState(Math.floor(Math.random() * (max - min + 1)) + min);
  let code = `${normalizeCode(watch('name'))}-${num1}-${num2}`;

  const onSubmit = async (data) => {
    setIsSending(true);

    // Makes a copy of the beginDate object to calculate its ending
    const endDate = new Date(+data.beginDate);

    let base64 = null;
    if (cmsType === 'WORDPRESS') {
      base64 = Buffer.from(`${data.cmsAuth.link}:${data.cmsAuth.password}`).toString('base64');
    }
    const account = {
      id: data.id,
      code,
      createdAt: data.createdAt,
      name: data.name,
      domain: data.domain,
      status: data.status.value,
      hasLogo: Boolean(file),
      beginDate: data.beginDate ? Date.parse(data.beginDate) : null,
      endDate:
        data.contractTime !== 0 ? endDate.setMonth(endDate.getMonth() + data.contractTime) : null,
      contractTime: data.contractTime,
      oneTime: data.oneTime,
      services: data.services.map(getFormValue),
      modules: data.modules.reduce((acc, cur) => ({ ...acc, [cur.value]: true }), {}),
      limits: {
        keywordReport: data.modules.some((module) => module.value === 'keywordReport')
          ? data.keywordReport
          : 0,
      },
      gscLink: data.gscLink || null,
      gaLink: data.gaLink || null,
      internalTeam: data.internalTeam.map(getFormValue),
      approvalTeam: data.approvalTeam.map(getFormValue),
      reviewer: data.reviewer ? getFormValue(data.reviewer) : null,
      contentManager: data.contentManager ? getFormValue(data.contentManager) : null,
      contactPoint: data.contactPoint || null,
      gDriveFolders: data.gDriveFolders || null,
      cmsAuth: {
        link: data.cmsAuth.link || null,
        login: data.cmsAuth.login || null,
        base64: base64 ? base64 : null,
        type: cmsType || null,
        key: data.cmsAuth.key || null,
      },
      keywordReportConfig: {
        country: data.country?.value || null,
        language: data.language?.value || null,
      },
    };

    try {
      const newModules = new Set(data.modules?.map((m) => m.value));
      const hadKeywordReportModule = oldModules.includes('keywordReport');
      const hasKeywordReportModule = newModules.has('keywordReport');

      const hadContentModule = oldModules.includes('content');
      const hasContentModule = newModules.has('content');

      setIsAccountDeactivated(!data.status.value);

      if (data.status.value) {
        await firebaseHelper.activateAccount(account.id);
      } else {
        await firebaseHelper.deactivateAccount(account.id);
        if (hasKeywordReportModule || hasContentModule) {
          await axios.delete(
            `${process.env.REACT_APP_BIG_SERPER_API_URL}/clients/ectools/${account.id}`,
            {
              headers: {
                Authorization: `Bearer ${process.env.REACT_APP_BIG_SERPER_API_KEY}`,
              },
            },
          );
          if (hasKeywordReportModule) {
            await firebaseHelper.deleteAccountKeywordGroup(account.id);
          }
        }
      }

      if (
        (hasKeywordReportModule && !hadKeywordReportModule) ||
        (hasContentModule && !hadContentModule)
      ) {
        await axios.post(
          `${process.env.REACT_APP_BIG_SERPER_API_URL}/clients`,
          {
            domain: data.domain,
            platform: 'ectools',
            client_id: account.id,
          },
          {
            headers: {
              Authorization: `Bearer ${process.env.REACT_APP_BIG_SERPER_API_KEY}`,
            },
          },
        );
        await firebaseHelper.createDefaultGroup(account.id, user.id);
      }

      const isDomainChanged = data.domain !== domain;

      if (isDomainChanged) {
        await axios.patch(
          `${process.env.REACT_APP_BIG_SERPER_API_URL}/clients/domain/ectools/${account.id}`,
          {
            domain: data.domain,
          },
          {
            headers: {
              Authorization: `Bearer ${process.env.REACT_APP_BIG_SERPER_API_KEY}`,
            },
          },
        );
      }

      if (changedFile) {
        await firebaseHelper.updateAccountLogo(file, account.id);
      }

      await firebaseHelper.updateAccount(account.id, account);

      setUpdateSuccessfull(true);
      setIsSending(false);
      setIsSaved(true);
      setModal(true);
    } catch (error) {
      setIsSending(false);
      setIsSaved(false);
      console.error(error);
      setErrorMessage('An error occurred while trying to update the account.');
      setErrorModal(true);
    }
  };

  const fetchAccountData = async () => {
    try {
      setIsSending(false);
      setIsLoading(true);
      const account = await firebaseHelper.getAccount(accountParam);
      setIsAccountDeactivated(!account.status);

      const processedServices = account.services.map((service) => {
        const matched = accountServices.find((s) => s.value === service);
        return {
          value: service,
          label: matched?.label ?? translate(service),
        };
      });
      const processedModules = Object.keys(account.modules).map((key) => {
        const capitalized = key.charAt(0).toUpperCase() + key.slice(1);
        return { value: key, label: translate(capitalized) };
      });
      const processedInternalTeam = await Promise.all(
        account.internalTeam.map(async (personId) => ({
          value: personId,
          label: await firebaseHelper.userName(personId),
        })),
      );
      const processedApprovalTeam = await Promise.all(
        (account.approvalTeam || []).map(async (personId) => ({
          value: personId,
          label: await firebaseHelper.userName(personId),
        })),
      );

      const processedContentManager = account.contentManager
        ? {
            value: account.contentManager,
            label: await firebaseHelper.userName(account.contentManager),
          }
        : null;
      const processedReviewer = account.reviewer
        ? {
            value: account.reviewer,
            label: await firebaseHelper.userName(account.reviewer),
          }
        : null;

      const processedCountry = account.keywordReportConfig?.country
        ? {
            value: account.keywordReportConfig.country,
            label: countrySelectOptions.find(
              (country) => country.value === account.keywordReportConfig.country,
            ).label,
          }
        : null;

      const processedLanguage = account.keywordReportConfig?.language
        ? {
            value: account.keywordReportConfig.language,
            label: languageSelectOptions.find(
              (language) =>
                language.value.toLowerCase() === account.keywordReportConfig.language.toLowerCase(),
            ).label,
          }
        : null;

      reset({
        id: account.id,
        createdAt: account.createdAt,
        hasLogo: account.hasLogo,
        name: account.name,
        domain: account.domain,
        status: {
          value: account.status,
          label: translate(account.status ? 'Activated' : 'Deactivated'),
        },
        beginDate: account.beginDate ? new Date(account.beginDate) : '',
        contractTime: account.contractTime,
        oneTime: account.oneTime,
        services: processedServices,
        modules: processedModules,
        keywordReport: account.limits?.keywordReport,
        internalTeam: processedInternalTeam,
        approvalTeam: processedApprovalTeam,
        contentManager: processedContentManager,
        reviewer: processedReviewer,
        gscLink: account.gscLink ?? '',
        gaLink: account.gaLink ?? '',
        contactPoint: account.contactPoint,
        country: processedCountry,
        language: processedLanguage,
        gDriveFolders: account.gDriveFolders ?? '',
        cmsAuth: {
          link: account.cmsAuth?.link ?? '',
          login: account.cmsAuth?.login ?? '',
          base64: account.cmsAuth?.base64 ?? '',
          type: account.cmsAuth?.type ?? '',
          key: account.cmsAuth?.key ?? '',
        },
      });
      if (account.cmsAuth?.base64 || account.cmsAuth?.key) {
        setIsSaved(true);
      }

      setCmsType(account.cmsAuth);
      setCode(account.code);
      setOldModules(processedModules.map((module) => module.value));
      setDomain(account.domain);
      setUpdateSuccessfull(true);
      setGDriveFolders(account.gDriveFolders);
      setIsLoading(false);

      if (account.hasLogo) {
        activateLoader();
        const path = `${account.id}/logo`;
        const profilePicMeta = await firebaseHelper.listAllFiles(path);
        if (profilePicMeta) {
          const profilePicFile = await firebaseHelper.getFileObj(
            `${path}/${profilePicMeta[0].name}`,
          );
          handleFileSubmit(profilePicFile, false);
        }
      }
    } catch (error) {
      setIsLoading(false);
      console.error(error);
      if (error === "This account doesn't exist") {
        setErrorMessage(
          "This account doesn't exist! Return to the Account Listing to try to edit another account.",
        );
      } else {
        setErrorMessage('Return to the Account Listing to try to edit another account.');
      }
      setErrorModal(true);
    }
  };

  const onInvalid = (errors) => {
    if (Object.values(errors).length || !updateSuccessfull) {
      setUpdateSuccessfull(false);
      console.error('Error saving changes');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
  };

  const handleFileSubmit = (photoFile) => {
    const loader = document.getElementById('loading-icon');
    const ghost = document.getElementById('ghost-icon');
    const image = document.getElementById('file-display');

    // Checks if a file was uploaded and if it matches the valid extensions and puts it as source for the img tag
    if (photoFile) {
      if (
        !(
          photoFile.type === 'image/png' ||
          photoFile.type === 'image/jpg' ||
          photoFile.type === 'image/jpeg'
        )
      ) {
        alert(
          translate('The profile photo file extesion needs to be one of the following') +
            ': png, jpg, jpeg.',
        );
        document.getElementById('file-input').value = null;
      } else if (photoFile.size > FILE_SIZE_5MB) {
        alert(translate('Max size of file is 5MB'));
        document.getElementById('file-input').value = null;
      } else {
        setChangedFile(true);
        setFile(photoFile);
        if (image) image.style.display = 'inline';
        if (image) image.src = URL.createObjectURL(photoFile);
        if (ghost) ghost.style.display = 'none';
        if (loader) loader.style.display = 'none';
      }
    } else {
      setFile(null);
      if (ghost) ghost.style.display = 'inline';
      if (image) image.style.display = 'none';
      if (loader) loader.style.display = 'none';
    }
  };

  const handleRemoveFile = (event) => {
    event.preventDefault();
    setFile(null);
    handleFileSubmit(null);
    setChangedFile(true);
    document.querySelector('#file-input').value = null;
  };

  const closeModalHandle = () => {
    setModal(false);
  };

  const randomizeCode = () => {
    let newNum1 = Math.floor(Math.random() * (max - min + 1)) + min;
    let newNum2 = Math.floor(Math.random() * (max - min + 1)) + min;
    while (newNum1 === num1) {
      newNum1 = Math.floor(Math.random() * (max - min + 1)) + min;
    }
    while (newNum2 === num2) {
      newNum2 = Math.floor(Math.random() * (max - min + 1)) + min;
    }
    setNum1(newNum1);
    setNum2(newNum2);
  };

  const setCode = (code) => {
    const firstNum = code.slice(-11, -6);
    const secondNum = code.slice(-5);
    setNum1(firstNum);
    setNum2(secondNum);
  };

  const getInternalTeam = () => {
    return new Promise(async (resolve, reject) => {
      const ectoTeam = await firebaseHelper.getTeam(process.env.REACT_APP_ECTO_ID);
      const select = Object.entries(ectoTeam).map(([accountId, memberInfo]) => ({
        value: memberInfo.id,
        label: `${memberInfo.name} ${memberInfo.lastName}`,
      }));
      resolve(select);
    });
  };

  const getApprovalTeam = () => {
    return new Promise(async (resolve, reject) => {
      const approvalTeam = teamData.filter(
        (user) =>
          user.adminStatus === 'Client' ||
          user.adminStatus === 'Client Admin' ||
          user.adminStatus === 'Super Admin',
      );
      const ectoTeam = await firebaseHelper.getTeam(process.env.REACT_APP_ECTO_ID);

      const selectTeamData = Object.entries(approvalTeam).map(([accountId, memberInfo]) => ({
        value: memberInfo.id,
        label: `${memberInfo.name} ${memberInfo.lastName}`,
      }));
      const selectEctoTeam = Object.entries(ectoTeam).map(([accountId, memberInfo]) => ({
        value: memberInfo.id,
        label: `${memberInfo.name} ${memberInfo.lastName}`,
      }));

      const allSelects = selectTeamData.concat(selectEctoTeam);
      const uniqueSelects = [];
      const userIds = new Set();
      allSelects.forEach((user) => {
        if (!userIds.has(user.value)) {
          uniqueSelects.push(user);
          userIds.add(user.value);
        }
      });
      resolve(uniqueSelects);
    });
  };

  const getAccountTeam = () => {
    return new Promise(async (resolve, reject) => {
      const team = await firebaseHelper.getTeam(accountParam);
      if (team) {
        const select = Object.entries(team)
          .filter(([memberId]) => memberId !== 'initialTeamState')
          .map(([accountId, memberInfo]) => ({
            value: memberInfo.id,
            label: `${memberInfo.name} ${memberInfo.lastName}`,
          }));
        resolve(select);
      }
    });
  };

  const fetchTeamTable = async () => {
    const team = await firebaseHelper.getTeam(accountParam);
    if (team) {
      const processedTeam = await Promise.all(
        Object.values(team)
          .filter((user) => user.id !== 'initialTeamState')
          .map(async (user) => {
            const userData = await firebaseHelper.getUser(user.id);
            return {
              id: user.id,
              name: `${user.name} ${user.lastName}`,
              email: userData.email,
              adminStatus: userData.adminStatus,
            };
          }),
      );
      setTeamData(processedTeam);
    }
  };

  const columns = [
    {
      dataField: 'name',
      text: translate('Name'),
      sort: true,
    },
    {
      dataField: 'email',
      text: translate('Email'),
      sort: true,
    },
  ];

  const defaultSorted = [
    {
      dataField: 'name',
      order: 'asc',
    },
  ];

  const sizePerPageList = [{ text: '10', value: 10 }];

  const pageOptions = {
    sizePerPage: 10,
    totalSize: teamData.length,
    custom: true,
    sizePerPageList,
  };

  const activateLoader = () => {
    const loader = document.getElementById('loading-icon');
    const ghost = document.getElementById('ghost-icon');
    const image = document.getElementById('file-display');
    if (loader) {
      loader.style.display = 'inline';
    }
    if (ghost) {
      ghost.style.display = 'none';
    }

    if (image) {
      image.style.display = 'none';
    }
  };

  useEffect(async () => {
    fetchAccountData();
    fetchTeamTable();
  }, []);

  useEffect(() => {
    if (!teamData) {
      return;
    }
    setSelectKey((prevState) => prevState + 1);
  }, [teamData]);

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>{translate('Edit Account')} | Ectools</title>
        </MetaTags>

        <Container fluid>
          <Breadcrumbs title={translate('Accounts')} breadcrumbItem={translate('Edit')} />

          <Row>
            <Col lg="12">
              <Card>
                <CardBody style={{ position: 'relative' }}>
                  <Modal
                    isOpen={modal}
                    role="dialog"
                    autoFocus={true}
                    centered={true}
                    className="exampleModal"
                    tabIndex="-1"
                  >
                    <ModalHeader>
                      <div className="modal--header">
                        <i className="bx bx-check-double modal--icon" />
                        <span className="modal--title">
                          {translate('Account Updated Successfully!')}
                        </span>
                      </div>
                    </ModalHeader>
                    <ModalBody>
                      <p>
                        {translate(
                          'The Account was successfully updated. See the accounts to view your changes.',
                        )}
                      </p>
                    </ModalBody>
                    <ModalFooter>
                      <Link to="/list-accounts">
                        <button className="modal--button-two">{translate('See Accounts')}</button>
                      </Link>
                      <button className="modal--button-one" onClick={closeModalHandle}>
                        {translate('Close')}
                      </button>
                    </ModalFooter>
                  </Modal>
                  <Modal
                    isOpen={errorModal}
                    role="dialog"
                    autoFocus={true}
                    centered={true}
                    className="exampleModal"
                    tabIndex="-1"
                  >
                    <ModalHeader>
                      <div className="modal--header">
                        <i className="bx bx-error-circle modal--icon" style={{ color: 'red' }} />
                        <span className="modal--title">
                          {translate('An Unexpected Error Occured!')}
                        </span>
                      </div>
                    </ModalHeader>
                    <ModalBody>
                      <p>{translate(errorMessage)}</p>
                    </ModalBody>
                    <ModalFooter>
                      <Link to="/list-accounts">
                        <button className="modal--button-two">{translate('Return')}</button>
                      </Link>
                    </ModalFooter>
                  </Modal>
                  <CardTitle className="mb-2 fw-normal" style={{ fontSize: '1.01rem' }}>
                    {translate('Edit Existing Account')}
                  </CardTitle>
                  <NavStyles tabs>
                    <NavItem>
                      <NavLink
                        className={activeNav === '1' ? 'actived' : ''}
                        onClick={(e) => {
                          e.preventDefault();
                          setActiveNav('1');
                        }}
                      >
                        {translate('Information')}
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={activeNav === '2' ? 'actived' : ''}
                        onClick={(e) => {
                          e.preventDefault();
                          setActiveNav('2');
                        }}
                      >
                        {translate('Directories')}
                      </NavLink>
                    </NavItem>
                  </NavStyles>
                  <FormProvider {...form}>
                    <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
                      <TabContent activeTab={activeNav}>
                        <TabPane tabId="1">
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'row',
                              justifyContent: 'space-between',
                            }}
                          >
                            {isLoading ? (
                              <Shimmer width="49%" height="65em" />
                            ) : (
                              <div style={{ width: '49%' }}>
                                <LabelGroup
                                  htmlFor="name"
                                  label="Account's Name"
                                  mb={1}
                                  column
                                  required
                                >
                                  <TextInput
                                    controlName="name"
                                    placeholder="Name"
                                    validation={{
                                      pattern: {
                                        value: /[A-Za-z_]/,
                                        message: 'Name must contain at least a letter!',
                                      },
                                    }}
                                    disabled={isAccountDeactivated}
                                    noEllipsis
                                    required
                                  />
                                  {errors.name && (
                                    <span style={{ color: 'red' }}>
                                      {translate(errors.name.message)}
                                    </span>
                                  )}
                                </LabelGroup>
                                <LabelGroup htmlFor="domain" label="Domain" mb={1} column required>
                                  <TextInput
                                    controlName="domain"
                                    placeholder=""
                                    validation={{
                                      pattern: {
                                        value: /^(?!:\/\/)([a-zA-Z0-9-]{1,63}\.)+[a-zA-Z]{2,63}$/,
                                        message: 'Enter a valid domain!',
                                      },
                                    }}
                                    disabled={isAccountDeactivated}
                                    noEllipsis
                                    required
                                  />
                                  {errors.domain && (
                                    <span style={{ color: 'red' }}>
                                      {translate(errors.domain.message)}
                                    </span>
                                  )}
                                </LabelGroup>

                                <LabelGroup htmlFor="status" label="Status" mb={1} column required>
                                  <SelectInput
                                    controlName="status"
                                    options={[
                                      { value: true, label: translate('Activated') },
                                      {
                                        value: false,
                                        label: translate('Deactivated'),
                                      },
                                    ]}
                                    required
                                  />
                                  {errors.status && (
                                    <span style={{ color: 'red' }}>
                                      {translate(errors.status.message)}
                                    </span>
                                  )}
                                </LabelGroup>

                                <FormGroup className="mt-3" row>
                                  <Col lg="2">
                                    <div
                                      style={{
                                        height: '6rem',
                                        width: '6rem',
                                        border: '1px solid #CED4DA',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        borderRadius: '4px',
                                      }}
                                    >
                                      <i
                                        id="ghost-icon"
                                        className="bx bx-ghost"
                                        style={{ fontSize: '4rem', color: '#D2D2D2' }}
                                      />

                                      <i
                                        id="loading-icon"
                                        className="bx bx-loader-alt loading-animation"
                                        style={{
                                          fontSize: '2rem',
                                          color: '#D2D2D2',
                                          display: 'none',
                                        }}
                                      />

                                      <img
                                        id="file-display"
                                        width="90%"
                                        style={
                                          file === null
                                            ? { display: 'none' }
                                            : {
                                                display: 'inline',
                                                maxHeight: '95%',
                                                objectFit: 'cover',
                                                objectPosition: 'center center',
                                              }
                                        }
                                      />
                                    </div>
                                  </Col>
                                  <Col lg="9">
                                    <Col
                                      style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                      }}
                                    >
                                      <Label
                                        style={{
                                          fontSize: '13px',
                                          height: '26px',
                                          color: '#495057',
                                          fontWeight: '400',
                                        }}
                                      >
                                        {translate('Logo')}
                                      </Label>
                                      {Boolean(file) && (
                                        <button
                                          className="remove-file-btn"
                                          onClick={handleRemoveFile}
                                          disabled={isAccountDeactivated}
                                        >
                                          <box-icon
                                            type="regular"
                                            name="trash"
                                            color="#F46A6A"
                                            size="1.3rem"
                                          />
                                          <span>{translate('Remove Image')}</span>
                                        </button>
                                      )}
                                    </Col>
                                    <Input
                                      id="file-input"
                                      className="form-control"
                                      type="file"
                                      onChange={(event) => handleFileSubmit(event.target.files[0])}
                                      accept="image/png, image/jpg, image/jpeg"
                                      disabled={isAccountDeactivated}
                                    />
                                    <p className="upload-photo-default-caption">
                                      {translate('Upload Photo Default Caption')}
                                    </p>
                                  </Col>
                                </FormGroup>

                                <LabelGroup
                                  htmlFor="beginDate"
                                  label="Begin Date"
                                  mb={1}
                                  column
                                  required
                                >
                                  <DateInput
                                    controlName="beginDate"
                                    placeholder="Account's Beginning Date"
                                    disabled={isAccountDeactivated}
                                    required
                                  />
                                  {errors.beginDate && (
                                    <span style={{ color: 'red' }}>
                                      {translate(errors.beginDate.message)}
                                    </span>
                                  )}
                                </LabelGroup>

                                <LabelGroup
                                  htmlFor="contractTime"
                                  label="Contract Time (months)"
                                  mb={1}
                                  column
                                  required
                                >
                                  <div className="d-flex flex-row">
                                    <SpinnerInput
                                      controlName="contractTime"
                                      size={4}
                                      min={1}
                                      disabled={isAccountDeactivated}
                                      required
                                    />
                                    <Controller
                                      control={control}
                                      name="oneTime"
                                      render={({ field }) => (
                                        <input
                                          {...field}
                                          style={{
                                            alignSelf: 'center',
                                            marginLeft: '1em',
                                          }}
                                          name="oneTime"
                                          type="checkbox"
                                          disabled={isAccountDeactivated}
                                        />
                                      )}
                                    />
                                    <label
                                      style={{
                                        margin: '0 0 0 1em',
                                        alignSelf: 'center',
                                      }}
                                      htmlFor="oneTime"
                                    >
                                      {translate(
                                        'This is a one-time contract or without renovation',
                                      )}
                                    </label>
                                  </div>
                                  {errors.contractTime && (
                                    <span style={{ color: 'red' }}>
                                      {translate(errors.contractTime.message)}
                                    </span>
                                  )}
                                </LabelGroup>

                                <LabelGroup
                                  htmlFor="services"
                                  label="Contracted Services"
                                  mb={1}
                                  column
                                  required
                                >
                                  <SelectInput
                                    controlName="services"
                                    isMulti
                                    options={accountServices}
                                    isDisabled={isAccountDeactivated}
                                    required
                                  />
                                  {errors.services && (
                                    <span style={{ color: 'red' }}>
                                      {translate(errors.services.message)}
                                    </span>
                                  )}
                                </LabelGroup>

                                <LabelGroup
                                  htmlFor="modules"
                                  label="Contracted Modules"
                                  mb={1}
                                  column
                                  required
                                >
                                  <SelectInput
                                    controlName="modules"
                                    isMulti
                                    options={accountModules}
                                    isDisabled={isAccountDeactivated}
                                    required
                                  />
                                  {errors.modules && (
                                    <span style={{ color: 'red' }}>
                                      {translate(errors.modules.message)}
                                    </span>
                                  )}
                                </LabelGroup>
                                {watch('modules').some(
                                  (module) => module.value === 'keywordReport',
                                ) && (
                                  <div className="my-3">
                                    <LabelGroup
                                      htmlFor=""
                                      label="Keyword Report Settings"
                                      mb={0}
                                      column
                                    />
                                    <div
                                      className="d-flex w-100 gap-3 rounded-3"
                                      style={{
                                        maxWidth: '91.5%',
                                        backgroundColor: '#F8F9FA',
                                        padding: '1rem',
                                      }}
                                    >
                                      <div className="w-100 m-0">
                                        <LabelGroup
                                          htmlFor="country"
                                          label="Country"
                                          mb={0}
                                          column
                                          required
                                          size={12}
                                          style={{ paddingTop: '0' }}
                                        >
                                          <SelectInput
                                            controlName="country"
                                            placeholder="Selecione"
                                            required
                                            options={countrySelectOptions}
                                            isDisabled={isAccountDeactivated}
                                          />
                                          {errors.country && (
                                            <span style={{ color: 'red' }}>
                                              {translate(errors.country.message)}
                                            </span>
                                          )}
                                        </LabelGroup>
                                      </div>
                                      <div className="w-100 m-0">
                                        <LabelGroup
                                          htmlFor="language"
                                          label="Language"
                                          mb={0}
                                          column
                                          required
                                          size={12}
                                          style={{ paddingTop: '0' }}
                                        >
                                          <SelectInput
                                            controlName="language"
                                            placeholder="Selecione"
                                            required
                                            options={languageSelectOptions}
                                            isDisabled={isAccountDeactivated}
                                          />
                                          {errors.language && (
                                            <span style={{ color: 'red' }}>
                                              {translate(errors.language.message)}
                                            </span>
                                          )}
                                        </LabelGroup>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {watch('modules').some(
                                  (module) => module.value === 'keywordReport',
                                ) && (
                                  <LabelGroup htmlFor="limits" label="Limits" mb={1} column>
                                    <SpinnerInput
                                      controlName="keywordReport"
                                      label="Report Keywords"
                                      size={12}
                                      step={10}
                                      min={1}
                                      mb={2}
                                      validation={{
                                        min: { value: 1, message: 'The value cannot be 0.' },
                                      }}
                                      disabled={isAccountDeactivated}
                                    />
                                    {errors.keywordReport && (
                                      <span style={{ color: 'red' }}>
                                        {translate(errors.keywordReport.message)}
                                      </span>
                                    )}
                                  </LabelGroup>
                                )}
                              </div>
                            )}

                            <div
                              style={{
                                height: 'auto',
                                width: '1px',
                                backgroundColor: '#E2E2E2',
                              }}
                            />

                            {isLoading ? (
                              <Shimmer width="49%" height="65em" />
                            ) : (
                              <div style={{ width: '49%' }}>
                                <FormGroup className="mb-1" row>
                                  <Col lg="11">
                                    <Label className="col-form-label col-lg-12">
                                      {translate('Client Code')}
                                    </Label>
                                    <div style={{ display: 'flex' }}>
                                      <Col lg="9">
                                        <input
                                          type="text"
                                          className="form-control"
                                          readOnly
                                          value={code}
                                          disabled={isAccountDeactivated}
                                        />
                                      </Col>
                                      <Col
                                        lg="3"
                                        style={{
                                          display: 'flex',
                                          boxSizing: 'border-box',
                                        }}
                                      >
                                        <Button
                                          style={{
                                            width: '4em',
                                            marginLeft: '1.5em',
                                            padding: '0 0',
                                            backgroundColor: '#F1B44C',
                                            border: 'none',
                                          }}
                                          onClick={randomizeCode}
                                          disabled={isAccountDeactivated}
                                        >
                                          <i
                                            className="bx bx-repost"
                                            style={{ fontSize: '1.5rem' }}
                                          />
                                        </Button>
                                        <Button
                                          style={{
                                            width: '4em',
                                            marginLeft: '1.5em',
                                            padding: '0 0',
                                            backgroundColor: '#596FDE',
                                            border: 'none',
                                          }}
                                          onClick={(e) => copyToClipBoard(e, code)}
                                          disabled={isAccountDeactivated}
                                        >
                                          <i
                                            className="bx bx-copy"
                                            style={{ fontSize: '1.5rem' }}
                                          />
                                        </Button>
                                      </Col>
                                    </div>
                                  </Col>
                                </FormGroup>

                                <LabelGroup
                                  htmlFor="contactPoint"
                                  label="Contact Point"
                                  mb={1}
                                  column
                                >
                                  <SelectInput
                                    asyncronous
                                    isMulti
                                    controlName="contactPoint"
                                    cacheOptions
                                    defaultOptions
                                    loadOptions={getAccountTeam}
                                    isDisabled={isAccountDeactivated}
                                  />
                                </LabelGroup>

                                <LabelGroup
                                  htmlFor="gscLink"
                                  label="Google Search Console Link"
                                  mb={1}
                                  column
                                >
                                  <TextInput
                                    controlName="gscLink"
                                    placeholder=""
                                    disabled={isAccountDeactivated}
                                    noEllipsis
                                  />
                                </LabelGroup>

                                <LabelGroup
                                  htmlFor="gaLink"
                                  label="Google Analytics Link"
                                  mb={1}
                                  column
                                >
                                  <TextInput
                                    controlName="gaLink"
                                    placeholder=""
                                    disabled={isAccountDeactivated}
                                    noEllipsis
                                  />
                                </LabelGroup>

                                <LabelGroup
                                  htmlFor="internalTeam"
                                  label="Ecto's Internal Team"
                                  mb={1}
                                  column
                                  required
                                >
                                  <SelectInput
                                    asyncronous
                                    isMulti
                                    controlName="internalTeam"
                                    cacheOptions
                                    defaultOptions
                                    loadOptions={getInternalTeam}
                                    isDisabled={isAccountDeactivated}
                                    required
                                  />
                                  {errors.internalTeam && (
                                    <span style={{ color: 'red' }}>
                                      {translate(errors.internalTeam.message)}
                                    </span>
                                  )}
                                </LabelGroup>

                                {watch('modules').some((module) => module.value === 'content') && (
                                  <>
                                    <LabelGroup
                                      htmlFor="approvalTeam"
                                      label="Approval Team"
                                      mb={1}
                                      column
                                      required
                                    >
                                      <SelectInput
                                        key={selectKey}
                                        asyncronous
                                        isMulti
                                        controlName="approvalTeam"
                                        cacheOptions
                                        defaultOptions
                                        required
                                        loadOptions={getApprovalTeam}
                                        isDisabled={isAccountDeactivated}
                                      />
                                      {errors.approvalTeam && (
                                        <span style={{ color: 'red' }}>
                                          {translate(errors.approvalTeam.message)}
                                        </span>
                                      )}
                                    </LabelGroup>
                                    <LabelGroup
                                      htmlFor="contentManager"
                                      label="Content responsible"
                                      mb={1}
                                      column
                                      required
                                    >
                                      <SelectInput
                                        key={selectKey}
                                        asyncronous
                                        controlName="contentManager"
                                        cacheOptions
                                        defaultOptions
                                        required
                                        loadOptions={getApprovalTeam}
                                        isDisabled={isAccountDeactivated}
                                      />
                                      {errors.contentManager && (
                                        <span style={{ color: 'red' }}>
                                          {translate(errors.contentManager.message)}
                                        </span>
                                      )}
                                    </LabelGroup>
                                    <LabelGroup
                                      htmlFor="reviewer"
                                      label="Review responsible"
                                      mb={1}
                                      column
                                      required
                                    >
                                      <SelectInput
                                        key={selectKey}
                                        asyncronous
                                        controlName="reviewer"
                                        cacheOptions
                                        defaultOptions
                                        required
                                        loadOptions={getApprovalTeam}
                                        isDisabled={isAccountDeactivated}
                                      />
                                      {errors.reviewer && (
                                        <span style={{ color: 'red' }}>
                                          {translate(errors.reviewer.message)}
                                        </span>
                                      )}
                                    </LabelGroup>
                                  </>
                                )}

                                <FormGroup className="mb-1" row>
                                  <Col lg="11">
                                    <Label className="col-form-label col-lg-12">
                                      {translate('Team')}
                                    </Label>
                                    <PaginationProvider
                                      pagination={paginationFactory(pageOptions)}
                                      keyField="id"
                                      columns={columns}
                                      data={teamData}
                                    >
                                      {({ paginationProps, paginationTableProps }) => (
                                        <ToolkitProvider
                                          keyField="id"
                                          columns={columns}
                                          data={teamData}
                                          search
                                        >
                                          {(toolkitProps) => (
                                            <React.Fragment>
                                              <Row>
                                                <Col xl="12">
                                                  <div className="table-responsive">
                                                    <BootstrapTable
                                                      keyField={'id'}
                                                      responsive
                                                      hover
                                                      bordered={false}
                                                      striped={false}
                                                      defaultSorted={defaultSorted}
                                                      classes={'table align-middle table-nowrap'}
                                                      headerWrapperClasses={'thead-light'}
                                                      {...toolkitProps.baseProps}
                                                      {...paginationTableProps}
                                                    />
                                                  </div>
                                                </Col>
                                              </Row>

                                              <Row className="align-items-md-center mt-30">
                                                <Col className="inner-custom-pagination d-flex">
                                                  <div className="d-inline">
                                                    <SizePerPageDropdownStandalone
                                                      {...paginationProps}
                                                    />
                                                  </div>
                                                  <div className="text-md-right ms-auto">
                                                    <PaginationListStandalone
                                                      {...paginationProps}
                                                    />
                                                  </div>
                                                </Col>
                                              </Row>
                                            </React.Fragment>
                                          )}
                                        </ToolkitProvider>
                                      )}
                                    </PaginationProvider>
                                    {watch('modules').some(
                                      (module) => module.value === 'content',
                                    ) && (
                                      <CardInputCMS
                                        accountId={accountParam}
                                        watch={watch}
                                        setValue={setValue}
                                        getValues={getValues}
                                        cmsType={cmsType}
                                        setCmsType={setCmsType}
                                        errors={errors}
                                        clearErrors={clearErrors}
                                        isSaved={isSaved}
                                        setIsSaved={setIsSaved}
                                        disabled={isAccountDeactivated}
                                      />
                                    )}
                                  </Col>
                                </FormGroup>
                              </div>
                            )}
                          </div>

                          <ButtonSubmitStyles
                            type="submit"
                            className="mt-3"
                            style={{
                              border: 'none',
                              minWidth: '10rem',
                              minHeight: '2.5rem',
                              maxHeight: '2.5rem',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: 0,
                              position: 'relative',
                            }}
                            disabled={isSending}
                          >
                            {isSending ? (
                              <div
                                className="spinner-border text-light"
                                role="status"
                                style={{
                                  width: '1.5rem',
                                  height: '1.5rem',
                                  position: 'absolute',
                                }}
                              >
                                <span className="visually-hidden">Loading...</span>
                              </div>
                            ) : (
                              translate('Save changes')
                            )}
                          </ButtonSubmitStyles>
                        </TabPane>
                        <TabPane tabId="2">
                          <div className="d-flex gap-3 flex-wrap justify-content-between">
                            {getValues('services').map((service, index) => {
                              const serviceMatch = accountServices.find(
                                (s) => s.value === service.value,
                              );

                              const serviceLabel = serviceMatch ? serviceMatch.label : service;

                              if (!serviceMatch) return null;

                              // In the business rule, there is no folder option for the Locasty service.
                              if (serviceLabel === 'Locasty') return null;

                              const folder = gDriveFolders[serviceMatch.value]?.root || null;

                              return (
                                <CardInputGoogleDrive
                                  key={serviceMatch.value + index}
                                  accountId={accountParam}
                                  accountName={watch('name')}
                                  service={serviceMatch.value}
                                  label={serviceLabel}
                                  folderLink={folder}
                                  disabled={isAccountDeactivated}
                                />
                              );
                            })}
                          </div>
                        </TabPane>
                      </TabContent>
                    </form>
                  </FormProvider>

                  <div
                    style={{
                      display: updateSuccessfull === false ? 'flex' : 'none',
                      flexDirection: 'column',
                      alignItems: 'center',
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      border: '1px solid #F46A6A',
                      borderRadius: '4px',
                      overflow: 'hidden',
                      boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
                    }}
                  >
                    <Alert
                      color="light"
                      style={{
                        border: 'none',
                        borderRadius: 0,
                        margin: 0,
                        width: '100%',
                        borderBottom: '1px solid #EAD2D6',
                        padding: '12px',
                      }}
                    >
                      {translate('Unable to save changes')}
                    </Alert>
                    <Alert
                      color="danger"
                      style={{
                        border: 'none',
                        borderRadius: 0,
                        margin: 0,
                        padding: '12px',
                        color: '#000',
                        fontWeight: 500,
                        paddingRight: '48px',
                      }}
                    >
                      {translate('One or more required fields are empty')}
                    </Alert>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

const ButtonSubmitStyles = styled(Button)`
  background-color: #34c38f;
  border: none;
  &:hover {
    background-color: #25855a;
  }
`;

const NavStyles = styled(Nav)`
  height: 40px;
  display: flex;
  gap: 24px;
  margin: 1.5rem 0;

  .nav-item {
    .active {
      width: 180px;
      padding: 16px;
      font-size: 16px;
      line-height: 24px;
      font-weight: 400;
    }
    .actived {
      color: #556ee6;
      font-weight: 500;
      border-bottom: 2px solid #556ee6;
    }
  }
`;

EditAccount.propTypes = {
  location: PropTypes.object,
  search: PropTypes.string,
};

export default EditAccount;
